import { useState, useEffect } from "react";
import { apiFetch } from "../services/api.js";

export default function FormClient({ onSuccess }) {
  const [name, setName] = useState("");
  const [cpfCnpj, setCpfCnpj] = useState("");
  const [quantidadeCupons, setQuantidadeCupons] = useState(1);
  const [campanhas, setCampanhas] = useState([]);
  const [selectedCampanhas, setSelectedCampanhas] = useState([]);
  const [error, setError] = useState("");
  const [sucesso, setSucesso] = useState("");
  const [criando, setCriando] = useState(false);
  const [documentoTipo, setDocumentoTipo] = useState(""); // "CPF" | "CNPJ" | ""

  useEffect(() => {
    async function carregarCampanhas() {
      try {
        const res = await apiFetch("campanhas");
        const campanhasAtivas = (res.campanhas || []).filter(
          (c) => c.ativo !== false && c.arquivado !== true,
        );
        setCampanhas(campanhasAtivas);
      } catch (err) {
        console.error("Erro ao carregar campanhas:", err);
      }
    }
    carregarCampanhas();
  }, []);

  // Format CPF/CNPJ: accepts only numbers and formats automatically
  function formatarDocumento(valor) {
    // Remove non-digits
    const apenas = valor.replace(/\D/g, "");

    // Auto-detect type and format
    if (apenas.length <= 11) {
      // CPF format: XXX.XXX.XXX-XX
      setDocumentoTipo("CPF");
      if (apenas.length === 0) return "";
      if (apenas.length <= 3) return apenas;
      if (apenas.length <= 6)
        return `${apenas.substring(0, 3)}.${apenas.substring(3)}`;
      if (apenas.length <= 9)
        return `${apenas.substring(0, 3)}.${apenas.substring(3, 6)}.${apenas.substring(6)}`;
      return `${apenas.substring(0, 3)}.${apenas.substring(3, 6)}.${apenas.substring(6, 9)}-${apenas.substring(9)}`;
    } else {
      // CNPJ format: XX.XXX.XXX/XXXX-XX
      setDocumentoTipo("CNPJ");
      if (apenas.length <= 2) return apenas;
      if (apenas.length <= 5)
        return `${apenas.substring(0, 2)}.${apenas.substring(2)}`;
      if (apenas.length <= 8)
        return `${apenas.substring(0, 2)}.${apenas.substring(2, 5)}.${apenas.substring(5)}`;
      if (apenas.length <= 12)
        return `${apenas.substring(0, 2)}.${apenas.substring(2, 5)}.${apenas.substring(5, 8)}/${apenas.substring(8)}`;
      return `${apenas.substring(0, 2)}.${apenas.substring(2, 5)}.${apenas.substring(5, 8)}/${apenas.substring(8, 12)}-${apenas.substring(12)}`;
    }
  }

  // Handle document input: format and search for existing client
  async function handleDocumentoChange(e) {
    const valor = e.target.value;
    const formatado = formatarDocumento(valor);
    setCpfCnpj(formatado);

    // Only search if document is complete (11 or 14 digits)
    const apenasNumeros = formatado.replace(/\D/g, "");
    if (apenasNumeros.length === 11 || apenasNumeros.length === 14) {
      try {
        // Try to find existing client by document
        const res = await apiFetch("clientes");
        const clientes = res.clientes || [];
        const clienteBuscado = clientes.find(
          (c) =>
            c.documento && c.documento.replace(/\D/g, "") === apenasNumeros,
        );
        if (clienteBuscado && clienteBuscado.nome) {
          setName(clienteBuscado.nome);
        }
      } catch (err) {
        console.error("Erro ao buscar cliente:", err);
      }
    }
  }

  function toggleCampanha(campanhaId) {
    setSelectedCampanhas((prev) =>
      prev.includes(campanhaId)
        ? prev.filter((id) => id !== campanhaId)
        : [...prev, campanhaId],
    );
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setSucesso("");
    setCriando(true);

    // Validate all fields
    const apenasNumeros = cpfCnpj.replace(/\D/g, "");
    const novasValidacoes = {
      nome: name.trim().length > 0,
      documento: apenasNumeros.length === 11 || apenasNumeros.length === 14,
      campanhas: selectedCampanhas.length > 0,
    };

    // Check if all validations pass
    if (
      !novasValidacoes.nome ||
      !novasValidacoes.documento ||
      !novasValidacoes.campanhas
    ) {
      const erros = [];
      if (!novasValidacoes.nome) erros.push("Nome é obrigatório");
      if (!novasValidacoes.documento)
        erros.push("CPF/CNPJ deve ter 11 ou 14 dígitos");
      if (!novasValidacoes.campanhas)
        erros.push("Selecione pelo menos uma campanha");
      setError(erros.join(" • "));
      setCriando(false);
      return;
    }

    try {
      if (quantidadeCupons < 1) {
        throw new Error("Quantidade de cupons deve ser maior que 0");
      }

      const resCliente = await apiFetch("clientes", {
        method: "POST",
        body: JSON.stringify({
          nome: name,
          cpf_cnpj: cpfCnpj,
        }),
      });

      const clienteId = resCliente.cliente.id;
      let cupomsCriados = 0;

      // Para cada campanha selecionada, adicionar N cupons como lote
      for (const campanhaId of selectedCampanhas) {
        const campanha = campanhas.find((c) => c.id === campanhaId);
        // Proteção extra: ignorar campanha deletada ou arquivada
        if (
          !campanha ||
          campanha.ativo === false ||
          campanha.arquivado === true
        )
          continue;

        // Usar novo endpoint de lote para adicionar múltiplos cupons
        const resLote = await apiFetch("cupons/lote", {
          method: "POST",
          body: JSON.stringify({
            clienteId,
            campanhaId,
            quantidade: quantidadeCupons,
          }),
        });
        cupomsCriados += resLote.criados || quantidadeCupons;
      }

      const msg = `✅ Cliente "${name}" cadastrado com sucesso! (${cupomsCriados} cupom${cupomsCriados > 1 ? "ns" : ""} distribuído${cupomsCriados > 1 ? "s" : ""})`;
      setSucesso(msg);

      setName("");
      setCpfCnpj("");
      setQuantidadeCupons(1);
      setSelectedCampanhas([]);
      setDocumentoTipo("");
      onSuccess && onSuccess();

      // Limpar mensagem após 4 segundos
      setTimeout(() => setSucesso(""), 4000);
    } catch (err) {
      setError(err.message || "Erro ao criar cliente");
    } finally {
      setCriando(false);
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <h3>Adicionar Cliente</h3>

      <div style={{ marginBottom: "1rem" }}>
        <label>
          CPF/CNPJ:{" "}
          {documentoTipo && (
            <span style={{ color: "#667eea" }}>({documentoTipo})</span>
          )}{" "}
          {cpfCnpj.replace(/\D/g, "").length === 11 ||
          cpfCnpj.replace(/\D/g, "").length === 14
            ? "✓"
            : "⚠"}
        </label>
        <input
          value={cpfCnpj}
          placeholder="000.000.000-00"
          onChange={handleDocumentoChange}
          disabled={criando}
          maxLength="18"
          style={{
            borderColor:
              cpfCnpj.replace(/\D/g, "").length === 0
                ? "#e74c3c"
                : cpfCnpj.replace(/\D/g, "").length === 11 ||
                    cpfCnpj.replace(/\D/g, "").length === 14
                  ? "#27ae60"
                  : "#f39c12",
            borderWidth: "2px",
            width: "100%",
            padding: "0.5rem",
            borderRadius: "4px",
          }}
        />
        <small
          style={{ color: "#666", display: "block", marginTop: "0.25rem" }}
        >
          {cpfCnpj.replace(/\D/g, "").length === 0
            ? "Digite um CPF (11 dígitos) ou CNPJ (14 dígitos)"
            : cpfCnpj.replace(/\D/g, "").length < 11
              ? `${11 - cpfCnpj.replace(/\D/g, "").length} dígitos faltando para CPF`
              : cpfCnpj.replace(/\D/g, "").length === 11
                ? "CPF completo ✓"
                : cpfCnpj.replace(/\D/g, "").length < 14
                  ? `${14 - cpfCnpj.replace(/\D/g, "").length} dígitos faltando para CNPJ`
                  : "CNPJ completo ✓"}
        </small>
      </div>

      <div style={{ marginBottom: "1rem" }}>
        <label>Nome: {name.trim().length > 0 ? "✓" : "⚠"}</label>
        <input
          value={name}
          placeholder="Digite o nome completo"
          onChange={(e) => setName(e.target.value)}
          disabled={criando}
          style={{
            borderColor: name.trim().length > 0 ? "#27ae60" : "#e74c3c",
            borderWidth: "2px",
            width: "100%",
            padding: "0.5rem",
            borderRadius: "4px",
          }}
        />
      </div>

      <div style={{ marginBottom: "1rem" }}>
        <label>Quantidade de Cupons por Campanha:</label>
        <input
          type="number"
          min="1"
          value={quantidadeCupons}
          onChange={(e) => setQuantidadeCupons(parseInt(e.target.value) || 1)}
          disabled={criando}
          style={{
            width: "100%",
            padding: "0.5rem",
            borderRadius: "4px",
            borderColor: "#ccc",
            borderWidth: "1px",
          }}
        />
      </div>

      {campanhas.length > 0 && (
        <div className="eventos-section" style={{ marginBottom: "1rem" }}>
          <label>
            Selecione as Campanhas (para receber cupons):{" "}
            {selectedCampanhas.length > 0 ? "✓" : "⚠"}
          </label>
          <div className="eventos-list" style={{ marginTop: "0.5rem" }}>
            {campanhas.map((campanha) => (
              <label
                key={campanha.id}
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  padding: "0.5rem",
                  background: selectedCampanhas.includes(campanha.id)
                    ? "#e8f4f8"
                    : "#f9f9f9",
                  borderRadius: "4px",
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedCampanhas.includes(campanha.id)}
                  onChange={() => toggleCampanha(campanha.id)}
                  disabled={criando}
                />{" "}
                {campanha.nome}
              </label>
            ))}
          </div>
        </div>
      )}

      <button
        type="submit"
        disabled={criando || selectedCampanhas.length === 0}
        style={{
          padding: "0.75rem 1.5rem",
          fontSize: "1rem",
          background:
            criando || selectedCampanhas.length === 0 ? "#ccc" : "#667eea",
          color: "white",
          border: "none",
          borderRadius: "4px",
          cursor:
            criando || selectedCampanhas.length === 0
              ? "not-allowed"
              : "pointer",
        }}
      >
        {criando ? "Criando cliente..." : "Salvar Cliente"}
      </button>

      {error && (
        <p
          style={{
            color: "#e74c3c",
            fontWeight: "600",
            marginTop: "1rem",
            padding: "0.75rem",
            background: "#fadbd8",
            borderRadius: "4px",
          }}
        >
          ⚠ {error}
        </p>
      )}
      {sucesso && (
        <p
          style={{
            color: "#27ae60",
            fontWeight: "600",
            marginTop: "1rem",
            padding: "0.75rem",
            background: "#d5f4e6",
            borderRadius: "4px",
          }}
        >
          {sucesso}
        </p>
      )}
    </form>
  );
}
